/**
 * Cuánto tarda una confirmación: el mínimo antes de pulsar, y lo que queda mientras corre (BB-1652).
 *
 * Se calcula con datos que YA están en el audit log —`iniciadaMs`, los `progress` con su
 * `nEnviados`, `resumen.nViajes`— así que no depende de nada nuevo del backend.
 *
 * El ritmo se mide en una VENTANA RECIENTE, no promediando desde el arranque. La diferencia no es
 * cosmética: en la confirmación de 342.185 viajes del 2026-08-13 se dobló el ritmo a mitad de carrera
 * (la concurrencia de la cola de Cloud Tasks pasó de 1 a 2), y el promedio desde el inicio seguía
 * diciendo 5 h 17 min cuando faltaban 3 h 09 min. Un promedio arrastra para siempre el pasado lento
 * —y, peor, diluye una ralentización real durante horas—, mientras que una ventana reciente responde
 * al ritmo que hay ahora. Si no hay ventana suficiente NO se estima: se cae al teórico, etiquetado
 * como mínimo.
 *
 * NUNCA se promedia desde `iniciadaMs`. Ese respaldo existió y estuvo mal desde BB-1667: `iniciadaMs`
 * es cuándo se escribió el `intent`, no cuándo empezó a pagarse, y con tandas que esperan en la cola
 * el divisor es casi todo espera. El 2026-08-14 anunciaba "~2 h 27 min" para 787 viajes que tardaron
 * menos de un minuto.
 *
 * TRES COSAS QUE ESTE MÓDULO SE NIEGA A HACER, y son la razón de que devuelva un tipo con variantes
 * en vez de un número:
 *
 *  - **No inventa un ritmo cuando no hay señal.** En los primeros segundos `nEnviados` es 0 o casi, y
 *    dividir por eso da un ritmo que es ruido. Si hay ritmo teórico (`limiteRpsPago`, del contrato)
 *    cae a él ETIQUETADO como teórico —"al menos X"—, y si tampoco lo hay dice "calculando", que es la
 *    verdad. Lo que no hace nunca es presentar una extrapolación de dos viajes como una medición.
 *  - **No sigue contando si el trabajo dejó de dar señales.** Un contador que baja mientras el worker
 *    está muerto es peor que no tener contador: afirma progreso que no existe. Con la última señal
 *    vieja devuelve `atascado` con cuánto lleva callado.
 *  - **No promete una cifra exacta.** Es un mínimo que se puede estirar: los reintentos de 429/5xx
 *    solo pueden alargarlo. La UI lo dice con "~".
 */

/** Sin señal durante esto, el ritmo deja de ser creíble y se dice que puede estar atascado. */
export const SEG_SIN_SENAL_PARA_SOSPECHAR = 120;

/** Con menos enviados que esto, el ritmo es ruido y no se estima nada. */
const MINIMO_ENVIADOS_PARA_ESTIMAR = 5;

/**
 * Ventana que se intenta medir. Diez minutos son ~65 chunks al ritmo de hoy: bastante para promediar
 * el ruido de un chunk lento, y poco para que un cambio de ritmo se note en minutos y no en horas.
 */
const VENTANA_OBJETIVO_MS = 10 * 60 * 1000;

/**
 * Y por debajo de esto la ventana no se usa, se cae al promedio: con menos de un minuto de recorrido,
 * dos latidos separados por unos segundos dan un ritmo que es ruido — el mismo motivo que
 * `MINIMO_MS_TRANSCURRIDOS`, aplicado a la ventana.
 */
const MINIMO_MS_VENTANA = 60_000;

/** Un `progress` visto desde aquí: cuándo se escribió y cuántos viajes iban enviados entonces. */
export interface MuestraProgreso {
  ms: number;
  nEnviados: number;
}

/**
 * Ritmo en la ventana reciente, o `null` si no hay con qué medirlo.
 *
 * Coge la muestra más reciente y la más antigua que esté a `VENTANA_OBJETIVO_MS` o más; si no hay
 * ninguna tan atrás, usa la más antigua disponible, que es lo mejor que se puede hacer al principio.
 */
export function ritmoReciente(
  muestras: readonly MuestraProgreso[] | undefined,
): { viajesPorMs: number; ventanaMs: number } | null {
  if (muestras === undefined || muestras.length < 2) return null;

  const orden = [...muestras].sort((a, b) => a.ms - b.ms);
  const ultima = orden[orden.length - 1]!;
  // La primera que cae dentro de la ventana; si ninguna, la más antigua que haya.
  const dentro = orden.filter((m) => ultima.ms - m.ms >= VENTANA_OBJETIVO_MS);
  const primera = dentro.length > 0 ? dentro[dentro.length - 1]! : orden[0]!;

  const ventanaMs = ultima.ms - primera.ms;
  const enviados = ultima.nEnviados - primera.nEnviados;
  if (ventanaMs < MINIMO_MS_VENTANA || enviados <= 0) return null;

  return { viajesPorMs: enviados / ventanaMs, ventanaMs };
}

/**
 * Cuánto tardaría, como MÍNIMO, confirmar N viajes — antes de pulsar nada (BB-1652, pieza 1).
 *
 * `nViajes / limiteRpsPago`, con el ritmo que llega del contrato (`limiteRpsPago`, BB-1651) y no como
 * constante duplicada aquí: el número vive en `functions/.env` y puede cambiar sin desplegar el front.
 *
 * Es un piso, no una previsión: el cliente de pago va secuencial, así que la latencia real de cada
 * POST se suma encima del rate-limit, y los reintentos de 429/5xx solo pueden alargarlo. Nunca
 * acortarlo. La UI lo dice con "al menos".
 */
export function etaTeoricoSegundos(nViajes: number, limiteRpsPago: number | undefined): number | null {
  if (nViajes <= 0) return null;
  if (limiteRpsPago === undefined || limiteRpsPago <= 0) return null;
  return Math.ceil(nViajes / limiteRpsPago);
}

export type Restante =
  /** Todavía no hay ritmo real, pero sí el teórico del contrato: se usa ese, marcado como tal. */
  | { tipo: 'teorico'; segundos: number }
  /** Todavía no hay con qué estimar, ni ritmo real ni teórico. */
  | { tipo: 'sin-senal' }
  /**
   * Hay ritmo real y esto es lo que falta, como mínimo. `ventanaSegundos` dice sobre qué se midió, y
   * ya no puede ser `null`: si no hay ventana, no hay estimación — se devuelve `teorico`. Antes `null`
   * significaba "promedio desde el arranque", que es justo el cálculo que se eliminó.
   */
  | { tipo: 'estimando'; segundos: number; viajesPendientes: number; ventanaSegundos: number }
  /** El ritmo dice que ya debería haber acabado, pero sigue en curso. */
  | { tipo: 'deberia-haber-acabado' }
  /** Lleva demasiado tiempo sin escribir un `progress`. */
  | { tipo: 'atascado'; segundosSinSenal: number };

export interface EntradaRestante {
  /** Viajes de la confirmación (`resumen.nViajes`). */
  total: number | null;
  /** Enviados según el último `progress` (`progreso.nEnviados`). */
  nEnviados: number | null;
  /**
   * Cuándo se escribió el `intent`. **No entra en el ritmo**, y no es un descuido: una confirmación
   * puede pasar horas encolada antes de enviar su primera tanda, así que dividir por el tiempo desde
   * aquí mide la espera, no el trabajo. Se conserva porque la fila lo usa para otras cosas.
   */
  iniciadaMs: number | null;
  /** Última señal conocida: el `progress` más reciente (`ultimaSenalMs`). */
  ultimaSenalMs: number | null;
  /**
   * Los `progress` de esta confirmación: la ÚNICA fuente de ritmo. Si no llegan, o no dan una ventana
   * suficiente, no se estima nada y se cae al teórico.
   */
  muestras?: readonly MuestraProgreso[];
  ahoraMs: number;
  /**
   * Ritmo teórico del contrato (`limiteRpsPago`). Se usa SOLO mientras no hay ritmo real: en los
   * primeros segundos, extrapolar de 2 viajes enviados da ruido, y el ritmo teórico al menos es un
   * suelo conocido. En cuanto hay señal real manda la real, que ya incluye la latencia y los
   * reintentos que el teórico ignora.
   */
  limiteRpsPago?: number;
}

export function calcularRestante(e: EntradaRestante): Restante {
  // El atasco se comprueba PRIMERO: si no hay señal reciente, da igual lo bueno que fuera el ritmo
  // hace diez minutos. Falla hacia la duda visible, igual que `frescura.ts`.
  if (e.ultimaSenalMs !== null) {
    const sinSenalMs = Math.max(0, e.ahoraMs - e.ultimaSenalMs);
    if (sinSenalMs > SEG_SIN_SENAL_PARA_SOSPECHAR * 1000) {
      return { tipo: 'atascado', segundosSinSenal: Math.floor(sinSenalMs / 1000) };
    }
  }

  // Sin ritmo real todavía: el teórico es mejor que nada, y va etiquetado como teórico para que la
  // UI no lo presente como una medición.
  const teorico = (): Restante => {
    const segundos = etaTeoricoSegundos(e.total ?? 0, e.limiteRpsPago);
    return segundos === null ? { tipo: 'sin-senal' } : { tipo: 'teorico', segundos };
  };

  if (e.total === null || e.nEnviados === null) return teorico();
  if (e.nEnviados < MINIMO_ENVIADOS_PARA_ESTIMAR) return teorico();

  const pendientes = e.total - e.nEnviados;
  if (pendientes <= 0) return { tipo: 'deberia-haber-acabado' };

  // El ritmo se mide SIEMPRE entre latidos, nunca desde el `intent`.
  //
  // Aquí había un respaldo que dividía `nEnviados / (ahora - iniciadaMs)`, y estaba mal desde
  // BB-1667: `iniciadaMs` es cuándo se escribió el `intent`, no cuándo empezó a pagarse. Con tandas
  // que esperan en la cola —por cuota agotada, o con la cola pausada— ese divisor es casi todo espera.
  // Visto en producción el 2026-08-14: 787 viajes anunciados como "~2 h 27 min" porque la
  // confirmación había pasado ocho horas encolada antes de enviar su primera tanda; 600 viajes entre
  // ocho horas son 0,02 viajes/s.
  //
  // Y no era un caso raro: `ritmoReciente` pide una ventana de 60 s, y una confirmación de 787 viajes
  // son 4 tandas que acaban en ~40 s, así que las pequeñas caían SIEMPRE en el respaldo. El camino
  // normal era el equivocado.
  //
  // Sin ventana medible no se inventa un ritmo: se cae al teórico, que va etiquetado "al menos" y es
  // un suelo honesto. Equivocarse hacia un mínimo demasiado corto es visible y se corrige en el
  // siguiente latido; anunciar dos horas y media para cincuenta segundos destruye la confianza en el
  // contador.
  const reciente = ritmoReciente(e.muestras);
  if (reciente === null) return teorico();

  return {
    tipo: 'estimando',
    segundos: Math.ceil(pendientes / reciente.viajesPorMs / 1000),
    viajesPendientes: pendientes,
    ventanaSegundos: Math.round(reciente.ventanaMs / 1000),
  };
}

/**
 * `'~4 min'`, `'~2 h 10 min'`, `'menos de 1 min'`.
 *
 * Sin librería de fechas y sin `Intl.RelativeTimeFormat`: esto no es una fecha relativa ("en 4
 * minutos"), es una duración, y el formato sigue el mismo tono llano que `describirTranscurrido` en
 * `frescura.ts`. La tilde va siempre: es un mínimo, no una promesa.
 */
export function formatearDuracion(segundos: number): string {
  if (segundos < 60) return 'menos de 1 min';

  const minutos = Math.round(segundos / 60);
  if (minutos < 60) return `~${minutos} min`;

  const horas = Math.floor(minutos / 60);
  const restoMin = minutos % 60;
  if (horas < 24) return restoMin === 0 ? `~${horas} h` : `~${horas} h ${restoMin} min`;

  const dias = Math.floor(horas / 24);
  const restoHoras = horas % 24;
  return restoHoras === 0 ? `~${dias} días` : `~${dias} días ${restoHoras} h`;
}

/**
 * Un mínimo, en palabras. Existe para no producir "al menos menos de 1 min", que es lo que sale de
 * pegar el prefijo a `formatearDuracion` sin mirar: por debajo del minuto la propia duración ya
 * expresa el mínimo y el prefijo sobra.
 */
export function describirMinimo(segundos: number): string {
  return segundos < 60 ? formatearDuracion(segundos) : `al menos ${formatearDuracion(segundos)}`;
}

/** Texto corto para la cabecera de la fila: lo que se lee sin abrir nada. */
export function describirRestanteCorto(r: Restante): string {
  switch (r.tipo) {
    case 'teorico':
      // "al menos" y no "quedan": todavía no se ha medido nada, es el suelo teórico.
      return describirMinimo(r.segundos);
    case 'sin-senal':
      return 'calculando…';
    case 'estimando':
      return `quedan ${formatearDuracion(r.segundos)}`;
    case 'deberia-haber-acabado':
      return 'terminando';
    case 'atascado':
      return 'sin señal';
  }
}
